/**
 * AdoraCare -- org-intake pending-clinic queue: pure view module
 * (ORG-INTAKE-QUEUE-V1, B2 / Story 5.4). Initially configured for COC in
 * the Partha workflow -- the queue's WORKING role is server-configured
 * (the org-default new_lead_followup routing_config row; default 'coc'),
 * so nothing here hardcodes COC-only behavior.
 *
 * All decision logic and copy live here as pure exports (the workboard /
 * lead-cockpit view-module idiom) so they are unit-testable via
 * react-dom/server + node without a browser.
 *
 * ACCESS DECISION: the frontend guard admits the ROUTABLE role superset
 * (receptionist / coc / regional_head / gm_agm / owner -- the same set
 * routing_config can name) and the BACKEND owner-config gate is the only
 * real authority: a routable role that is NOT the configured owner gets
 * the endpoint's 401, rendered as the honest access-restricted state.
 * This keeps the guard config-agnostic without a /me mirror; the nav
 * link, however, is hardcoded per-role today (CocNav + OwnerNav for the
 * Partha configuration) -- a re-configured role reaches the queue by URL
 * until nav goes config-aware (recorded gap, Story 5.4).
 *
 * HARD DATA RULE: every badge helper maps a REAL stored field (name /
 * source_normalized / src token from source_raw / requirement_bucket /
 * ctwa ad-click / created_at / B1 review reason). No score, no priority,
 * no fabricated field.
 */

import type { DashboardMe } from '@/dashboard/types';
import { clinicLabelOf } from '@/dashboard/clinic-context';

// ---- Backend contract mirrors (frontend-local per the CRM convention) ----

export type OrgIntakePhoneAvailability = 'available' | 'no_linked_phone';

export interface OrgIntakeReviewMarker {
  readonly reason: string;
  readonly clinicId: number | null;
  readonly at: string | null;
}

export interface OrgIntakeQueueRow {
  readonly leadId: number;
  readonly name: string | null;
  readonly sourceNormalized: string;
  readonly srcToken: string | null;
  readonly requirementBucket: string;
  readonly enquiryStatus: string;
  readonly adClick: boolean;
  readonly phoneAvailability: OrgIntakePhoneAvailability;
  readonly createdAt: string | null;
  readonly review: OrgIntakeReviewMarker | null;
}

export interface OrgIntakeClinicOption {
  readonly clinicId: number;
  readonly clinicLabel: string | null;
}

export interface OrgIntakeQueueResult {
  readonly role: DashboardMe['role'];
  readonly ownerRole: string;
  readonly rows: OrgIntakeQueueRow[];
  readonly isComplete: boolean;
  readonly clinics: OrgIntakeClinicOption[];
}

export interface SetOrgIntakeClinicResult {
  readonly status: 'clinic_set';
  readonly leadId: number;
  readonly clinicId: number;
  readonly clinicLabel: string | null;
  readonly keyMoved: boolean;
  readonly workActionStamped: boolean;
  /**
   * Server-derived: the assigned clinic is inside the CALLER's own
   * verified clinic scope. The detail link is offered ONLY when true --
   * the clinic-fenced lead surfaces would 401 an out-of-scope caller
   * (no dead links).
   */
  readonly clinicInCallerScope: boolean;
}

// ---- Route-guard decision (config-agnostic routable superset) ----

/**
 * The roles the frontend guard admits -- the ROUTABLE role set (verbatim
 * mirror of lib/work-routing/templates ROUTABLE_ROLES: every role the
 * routing config may name as the queue owner, plus 'owner' is already in
 * it). NOT an authorization list: the backend owner-config gate decides.
 */
export const ORG_INTAKE_GUARD_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>([
    'receptionist',
    'coc',
    'regional_head',
    'gm_agm',
    'owner',
  ]);

export interface OrgIntakeAccessState {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
}

export type OrgIntakeAccessDecision =
  | { kind: 'wait' }
  | { kind: 'redirect'; to: string }
  | { kind: 'render' };

/** Mirrors decideLeadCockpitAccess / decideWorkboardAccess exactly. */
export function decideOrgIntakeAccess(
  state: OrgIntakeAccessState,
): OrgIntakeAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!ORG_INTAKE_GUARD_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// ---- Copy (honest empty / ceiling / hint constants) ----

export const ORG_INTAKE_TITLE = 'Org intake -- leads waiting for a clinic';

export const ORG_INTAKE_HINT =
  'Campaign WhatsApp leads that have not named a clinic yet. Confirm the clinic once; the lead then moves into the normal clinic workflow.';

export const ORG_INTAKE_EMPTY_TITLE = 'No campaign leads waiting for a clinic.';

export const ORG_INTAKE_EMPTY_HINT =
  'Leads appear here once campaign WhatsApp capture is live for this organization.';

export const ORG_INTAKE_UNAVAILABLE_COPY =
  'The intake queue is unavailable right now.';

export const ORG_INTAKE_RESTRICTED_COPY =
  'This queue is worked by another role in your organization (set in Routing settings).';

export const ORG_INTAKE_SET_ONCE_NOTE =
  'The clinic is set once from this queue. Changing a set clinic later is a separate follow-up capability and is not built yet.';

export const ORG_INTAKE_TRUNCATED_NOTE =
  'Showing the oldest pending leads -- more may be waiting beyond this page.';

/** Truthful count label. */
export function pendingCountLabel(count: number): string {
  return `${count} pending ${count === 1 ? 'lead' : 'leads'}`;
}

/** '(No name)' is the honest badge for a nameless WhatsApp inbound. */
export const NO_NAME_LABEL = '(No name)';

// ---- Badge helpers (REAL fields only) ----

/**
 * Human label for a B1 reconciliation-review reason. Exact tokens map
 * directly; the two composite shapes ('downstream_work:<signal>' /
 * 'ambiguous_linkage:<signal>') are PREFIX-matched (the signal is a
 * probe-table token). Unknown tokens render as a generic review label --
 * never hidden, never invented.
 */
export function reviewReasonLabel(reason: string): string {
  if (reason === 'parallel_clinic_journey') {
    return 'Needs review: parallel clinic journey';
  }
  if (reason === 'multiple_pending') {
    return 'Needs review: multiple pending journeys';
  }
  if (reason.startsWith('downstream_work')) {
    return 'Needs review: existing work on this lead';
  }
  if (reason.startsWith('ambiguous_linkage')) {
    return 'Needs review: linkage unclear';
  }
  return 'Needs review';
}

/**
 * Requirement-bucket badge value: 'Other' is the B1 constant for "nothing
 * captured", so it is NOT shown as a badge (showing 'Other' on every row
 * would read as data where there is none). A future source that captures
 * a real bucket surfaces it verbatim.
 */
export function requirementBadge(requirementBucket: string): string | null {
  const trimmed = requirementBucket.trim();
  if (trimmed.length === 0 || trimmed === 'Other') return null;
  return trimmed;
}

/** Clinic label for a picker option / review marker, id-fallback honest. */
export function clinicOptionLabel(option: OrgIntakeClinicOption): string {
  return clinicLabelOf(option.clinicLabel);
}

// ---- Set-clinic error mapping (code-first, the workbench convention) ----

export interface OrgIntakeErrorMessage {
  readonly text: string;
  /** TRUE only for concurrency conflicts (offer the Refresh affordance). */
  readonly showRefresh: boolean;
}

function errorStatus(error: unknown): number | null {
  if (
    error !== null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    return (error as { status: number }).status;
  }
  return null;
}

function errorCode(error: unknown): string | null {
  if (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }
  return null;
}

/**
 * Human message for a failed set-clinic. PRECONDITION conflicts get
 * specific copy and NO Refresh; CONCURRENCY conflicts get the standard
 * "updated by someone else" copy + Refresh (the clinical-workbench
 * error-mapper convention).
 */
export function setClinicErrorMessage(error: unknown): OrgIntakeErrorMessage {
  const code = errorCode(error);
  if (code === 'clinic_already_set') {
    return {
      text: 'This lead already has a clinic set. Refresh to update the queue.',
      showRefresh: true,
    };
  }
  if (code === 'lead_not_open') {
    return {
      text: 'This lead is no longer open, so a clinic cannot be set from here.',
      showRefresh: true,
    };
  }
  if (code === 'parallel_clinic_journey_exists') {
    return {
      text: 'This person already has a journey at that clinic. The pending lead was left unchanged -- it stays here for review.',
      showRefresh: false,
    };
  }
  if (code === 'org_pending_clinic_conflict' || errorStatus(error) === 409) {
    return {
      text: 'This lead was just updated by someone else. Refresh and try again.',
      showRefresh: true,
    };
  }
  if (errorStatus(error) === 401) {
    return { text: ORG_INTAKE_RESTRICTED_COPY, showRefresh: false };
  }
  if (error instanceof Error && error.message) {
    return { text: error.message, showRefresh: false };
  }
  return {
    text: 'Could not set the clinic. Please try again.',
    showRefresh: false,
  };
}

/** True when a queue LOAD failure is the backend access gate (401). */
export function isAccessRestricted(error: unknown): boolean {
  return errorStatus(error) === 401;
}

// ---- Pure state transitions ----

/** Drop an assigned lead's row (it left the pending queue). New array. */
export function removeQueueRow(
  rows: readonly OrgIntakeQueueRow[],
  leadId: number,
): OrgIntakeQueueRow[] {
  return rows.filter((r) => r.leadId !== leadId);
}

/**
 * Success feedback lines after a set-clinic. The detail link is offered
 * by the CALLER only when the leads-workspace surface is enabled for this
 * user (me.leadsWorkspaceEnabled) -- the existing lead-detail route
 * cannot render otherwise, and a dead link is a fake affordance.
 */
export function setClinicSuccessText(
  result: SetOrgIntakeClinicResult,
): string {
  const clinicName = result.clinicLabel ?? `clinic #${result.clinicId}`;
  return `Clinic set: ${clinicName}. Lead #${result.leadId} is now in the clinic workflow.`;
}

/** The post-assignment detail href (safeLeadId = String(lead id)). */
export function assignedLeadDetailHref(
  leadId: number,
  clinicId: number,
): string {
  return `/dashboard/leads/${encodeURIComponent(String(leadId))}?clinicId=${encodeURIComponent(String(clinicId))}`;
}
